import CircularGauge, {
  Range,
  RangeContainer,
  Scale,
  Size,
  ValueIndicator
} from 'devextreme-react/circular-gauge';
import DataGrid, { Column, Item, Toolbar, type DataGridRef } from 'devextreme-react/data-grid';
import { useRef } from 'react';
import type { ChartThemeColors } from '../../charts/useChartThemeColors';
import { KpiTile } from '../../components/KpiTile';
import { OPS_SITE_GRID_PROPS, OpsGridToolbarItems, OpsSiteGridChrome } from '../../opsDataGrid';
import {
  formatActivityDateTime,
  formatSessionDuration,
  formatUserLabel,
  formatViewport
} from '../formatters';
import { LifecycleStackedChart, MultiSeriesTrendChart } from '../userActivityCharts';
import type {
  UserActivityAnalyticsSummarySection,
  UserActivityLive
} from '../types';
import { ReportChartCard } from './ReportChartCard';

const ACTIVE_SESSIONS_GRID_KEY = 'user-activity-active-sessions-grid';

export function SummaryReport({
  live,
  summary,
  reportingTimeZone,
  chartColors,
  liveBusy,
  onRefreshLive,
  showTenantColumn
}: {
  live: UserActivityLive | null;
  summary: UserActivityAnalyticsSummarySection;
  reportingTimeZone: string;
  chartColors: ChartThemeColors;
  liveBusy: boolean;
  onRefreshLive: () => void;
  showTenantColumn: boolean;
}) {
  const activeGridRef = useRef<DataGridRef>(null);
  const duration = summary.duration;
  const coverage = summary.coverage;

  return (
    <div className="user-activity-analytics__body user-activity-analytics__body--scroll">
      <section className="user-activity-analytics__kpi-row">
        <KpiTile label="Active now (live)" value={String(live?.activeNow ?? 0)} />
        <KpiTile label="Sessions today" value={String(live?.loginsToday ?? 0)} />
        <KpiTile label="Unique users today" value={String(live?.uniqueUsersToday ?? 0)} />
        <KpiTile
          label="Avg session today"
          value={
            live?.averageSessionMinutesToday != null
              ? `${live.averageSessionMinutesToday} min`
              : '—'
          }
        />
        <KpiTile label="Sessions in range" value={String(summary.sessionsInRange)} />
        <KpiTile label="Unique users in range" value={String(summary.uniqueUsersInRange)} />
        <KpiTile label="Total minutes" value={String(duration.totalMinutes)} />
        <KpiTile
          label="Avg / median / P90"
          value={
            duration.averageMinutes != null
              ? `${duration.averageMinutes} / ${duration.medianMinutes ?? '—'} / ${duration.p90Minutes ?? '—'} min`
              : '—'
          }
        />
        <KpiTile label="Sessions / user" value={String(summary.sessionsPerUser)} />
        <KpiTile label="Active days" value={String(summary.activeDaysInRange)} />
        <KpiTile label="Completion rate" value={`${summary.completionRate}%`} />
        <KpiTile label="Geo resolved" value={`${coverage.geoResolvedPercent}%`} />
      </section>

      {summary.dailyTrendCapped ? (
        <p className="user-activity-report-banner">
          Daily trend chart is capped to the latest 366 days. Totals still cover the full selected range.
        </p>
      ) : null}

      <section className="user-activity-analytics__split-card">
        <div className="user-activity-analytics__live-block">
          <div className="user-activity-gauge-wrap">
            <CircularGauge value={live?.activePercentToday ?? 0}>
              <Size width={145} height={145} />
              <Scale startValue={0} endValue={100} tickInterval={25} />
              <RangeContainer>
                <Range startValue={0} endValue={40} />
                <Range startValue={40} endValue={70} />
                <Range startValue={70} endValue={100} />
              </RangeContainer>
              <ValueIndicator type="triangleNeedle" />
            </CircularGauge>
          </div>
          <div>
            <h3 className="ops-section-title" style={{ margin: '0 0 0.35rem' }}>
              Active share today (live)
            </h3>
            <p style={{ margin: 0, color: 'var(--ops-color-muted, #6b7280)', fontSize: 14 }}>
              {live?.activeNow ?? 0} of {live?.uniqueUsersToday ?? 0} unique users seen today are
              currently active.
            </p>
          </div>
        </div>
        <ReportChartCard
          title="Activity in range"
          subtitle={`Sessions, unique users, and minutes by day (${reportingTimeZone}). Duration uses completed sessions only.`}
          empty={summary.daily.every((d) => d.sessions === 0)}
        >
          <div className="user-activity-analytics__chart-host">
            <MultiSeriesTrendChart data={summary.daily} colors={chartColors} />
          </div>
        </ReportChartCard>
      </section>

      <div className="user-activity-analytics__summary-row">
        <ReportChartCard
          title="Session lifecycle"
          subtitle="Open sessions are counted separately from completed duration percentiles."
        >
          <div className="user-activity-analytics__chart-host">
            <LifecycleStackedChart lifecycle={summary.lifecycle} colors={chartColors} />
          </div>
        </ReportChartCard>
        <section className="user-activity-report-card">
          <div className="user-activity-report-card__header">
            <h3 className="ops-section-title" style={{ margin: 0 }}>
              Data coverage
            </h3>
          </div>
          <div className="user-activity-coverage-grid">
            <div>Geo resolved: {coverage.geoResolvedSessions}</div>
            <div>Geo unresolved: {coverage.geoUnresolvedSessions}</div>
            <div>With roles: {coverage.withRoles}</div>
            <div>With tenant: {coverage.withTenant}</div>
            <div>With last path: {coverage.withLastPath}</div>
            <div>With build: {coverage.withBuild}</div>
            <div>With timezone: {coverage.withTimezone}</div>
            <div>With locale: {coverage.withLocale}</div>
            <div>Completed sessions: {duration.completedSessions}</div>
            <div>Open sessions: {duration.openSessions}</div>
          </div>
        </section>
      </div>

      <section className="user-activity-report-card user-activity-analytics__active-panel">
        <div className="user-activity-report-card__header">
          <h3 className="ops-section-title" style={{ margin: 0 }}>
            Currently active (live)
          </h3>
        </div>
        <div className="user-activity-analytics__active-grid">
          <DataGrid
            ref={activeGridRef}
            dataSource={live?.activeSessions ?? []}
            keyExpr="sessionKey"
            height="100%"
            width="100%"
            showBorders
            columnAutoWidth
            columnHidingEnabled
            allowColumnResizing
            allowColumnReordering
            columnResizingMode="widget"
            {...OPS_SITE_GRID_PROPS}
            noDataText="No active users right now."
          >
            <OpsSiteGridChrome
              stateStorageKey={ACTIVE_SESSIONS_GRID_KEY}
              searchPlaceholder="Search active sessions…"
              showPaging={false}
            />
            <Toolbar>
              <OpsGridToolbarItems
                gridRef={activeGridRef}
                loading={liveBusy}
                onRefresh={onRefreshLive}
                refreshHint="Refresh active sessions"
              />
              <Item name="searchPanel" location="after" />
            </Toolbar>
            <Column
              caption="User"
              minWidth={180}
              calculateDisplayValue={(row) => formatUserLabel(row.displayName, row.userName)}
            />
            <Column dataField="roles" caption="Roles" minWidth={120} />
            {showTenantColumn ? (
              <Column dataField="tenantId" caption="Tenant" minWidth={180} />
            ) : null}
            <Column dataField="ipAddress" caption="IP" width={130} />
            <Column
              caption="Viewport"
              width={100}
              calculateDisplayValue={(row) =>
                formatViewport(row.viewportWidth, row.viewportHeight)
              }
            />
            <Column dataField="geoCountry" caption="Geo" minWidth={100} />
            <Column dataField="deviceCategory" caption="Device" width={100} />
            <Column dataField="browser" caption="Browser" minWidth={120} />
            <Column
              caption="Session"
              width={110}
              calculateDisplayValue={(row) =>
                formatSessionDuration(row.startedAt, null, row.lastSeenAt)
              }
            />
            <Column
              dataField="lastSeenAt"
              caption="Last seen"
              width={170}
              calculateDisplayValue={(row) => formatActivityDateTime(row.lastSeenAt)}
            />
          </DataGrid>
        </div>
      </section>
    </div>
  );
}
